import React from 'react';
import { Navigate, Switch } from '@common/components';

import { PrivateRoute } from '@routes/private-route';
import { NoMatch } from '@routes/no-match';
import { OrganizationAdminSettingsRoutes } from './organization-admin-settings-routes';

import { ReportsPage } from 'works/pages/organization/reports';
import { TeamPage } from 'works/pages/organization/team';
import { StaffingRequestsPage } from 'works/pages/organization/staffing-requests';
import { StaffingRequestPage } from 'works/pages/organization/staffing-requests/staffing-request';
import { ShiftPage } from '@scheduling/pages/organization';
import { ShiftsAddMultipleSteps } from '@scheduling/components';
import { CreateStaffingRequestSelectTemplatePage } from 'works/pages/organization/staffing-requests/create-staffing-request-select-template';
import { CreateStaffingRequestFormPage } from 'works/pages/organization/staffing-requests/create-staffing-request-form';
import { ApplicationsPage } from 'works/pages/organization/applications';
import { CandidatePage } from 'works/pages/organization/applications/candidate';
import { AccountSettingsPage } from '@identity/pages/account-settings-page';
import { TermsAndConditionsPage } from '@identity/pages/terms-and-conditions-page';
import { BillingPage, InvoicePage } from '@pay-bill/pages/organization';
import {
  TimesheetsPage,
  WorkerTimesheetPage,
} from '@timekeeping/pages/organization';
import { AssignmentPage } from 'works/pages/organization/team/assignment-page';
import { OrganizationCompliancePage } from '@onboarding/pages/organization/organization-compliance-page';
import { WorkerUploadSteps } from '@scheduling/components/worker-upload-steps';
import { WorkerDetailsPage } from '@scheduling/components/worker-detail/worker-details-page';
import { UuidRegex } from '@common/services';
import { HomePage } from '@institutions/pages/home-page';
import { useCurrentMembership } from '@identity/hooks';
import {
  MissionControlStaffingPage,
  MissionControlCompliancePage,
  MissionControlBillingPage,
} from '@institutions/pages';

export function OrganizationRoutes() {
  const currentMembership = useCurrentMembership();

  const longTermEnabled = currentMembership.canShowLongTermAssignments;
  const canShowCompliancePage = currentMembership.canShowSidebarOnboardingItem;
  const canShowShifts = currentMembership.canShowShifts;
  const canShowSidebarTimesheetsItem =
    currentMembership.canShowSidebarTimesheetsItem;
  const canShowMissionControl = currentMembership.canShowMissionControl;

  return (
    <Switch>
      <PrivateRoute
        path="/works/admin"
        component={OrganizationAdminSettingsRoutes}
        menuItem="admin"
      />
      <PrivateRoute
        exact
        path="/works/compliance"
        component={() => <Navigate to="/works/compliance/expired" />}
        permitted={canShowCompliancePage}
      />
      <PrivateRoute
        path="/works/compliance/:tabKey(expiring|expired)"
        component={OrganizationCompliancePage}
        menuItem="compliance"
        permitted={canShowCompliancePage}
      />
      <PrivateRoute
        path="/works/onboarding/*"
        component={({ match }) => (
          <Navigate to={`/works/team/${match.params[0]}`} />
        )}
      />
      <PrivateRoute
        component={WorkerUploadSteps}
        path="/works/team/on_demand/upload"
        exact={true}
      />
      <PrivateRoute
        path={`/works/team/:type(long_term)/:id(${UuidRegex})/:tabKey(assignment|compliance|info|profile|notes)`}
        component={AssignmentPage}
        menuItem="team"
      />
      {/* @TODO: This route is too confuse and has too much responsability and variation. - WORK-6767 */}
      <PrivateRoute
        path={`/works/team/:type(on_demand)/:id(${UuidRegex})/:tabKey(units|assignments|on_demand|matches)/:resourceId(${UuidRegex})/:fixMeTabKey(assignment|compliance|info|profile|notes)`}
        component={AssignmentPage}
        menuItem="team"
      />
      <PrivateRoute
        exact
        path={`/works/team/:type(on_demand)/:id(${UuidRegex})/:tabKey(units|assignments|on_demand|matches)/:resourceId`}
        component={({ match: { params } }) => (
          <Navigate
            to={`/works/team/${params.type}/${params.id}/${params.tabKey}/${params.resourceId}/assignment`}
          />
        )}
      />
      <PrivateRoute
        path={`/works/team/:type(on_demand)/:id(${UuidRegex})/:tabKey(units|assignments|on_demand|matches|history)`}
        component={AssignmentPage}
        menuItem="team"
      />
      <PrivateRoute
        exact
        path={`/works/team/on_demand/:id(${UuidRegex})`}
        component={WorkerDetailsPage}
      />
      <PrivateRoute
        exact
        path={`/works/team/long_term/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate to={`/works/team/long_term/${params.id}/assignment`} />
        )}
      />
      <PrivateRoute
        path="/works/team/:type(long_term|on_demand)"
        component={TeamPage}
        menuItem="team"
      />
      <PrivateRoute
        exact
        path="/works/team"
        component={() => {
          const defaultTab = longTermEnabled ? 'long_term' : 'on_demand';
          return <Navigate to={`/works/team/${defaultTab}`} />;
        }}
      />

      <PrivateRoute
        path="/works/shifts/feed/add-multiple"
        component={ShiftsAddMultipleSteps}
        menuItem="shift"
        exact={true}
        permitted={canShowShifts}
      />
      <PrivateRoute
        path="/works/shifts/:type(feed|calendar|messages)"
        component={ShiftPage}
        menuItem="shift"
        permitted={canShowShifts}
      />
      <PrivateRoute
        exact
        path="/works/shifts"
        component={() => <Navigate to="/works/shifts/calendar" />}
        permitted={canShowShifts}
      />

      <PrivateRoute
        path={`/works/staffing_requests/:staffingRequestId(${UuidRegex})/candidates/:id(${UuidRegex})/:tab(summary|notes)`}
        component={CandidatePage}
        menuItem="staffing"
      />
      <PrivateRoute
        path={`/works/staffing_requests/:staffingRequestId(${UuidRegex})/candidates/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate
            to={`/works/staffing_requests/${params.staffingRequestId}/candidates/${params.id}/summary`}
          />
        )}
      />
      <PrivateRoute
        path="/works/staffing_requests/templates/:tabKey(long_term|on_demand)"
        component={CreateStaffingRequestSelectTemplatePage}
        menuItem="staffing"
      />
      <PrivateRoute
        exact
        path="/works/staffing_requests/templates"
        component={() => (
          <Navigate to="/works/staffing_requests/templates/long_term" />
        )}
      />
      <PrivateRoute
        path="/works/staffing_requests/new/:templateId"
        component={CreateStaffingRequestFormPage}
        menuItem="staffing"
      />
      <PrivateRoute
        path="/works/staffing_requests/requests/:tabKey(long_term|on_demand)"
        component={StaffingRequestsPage}
        menuItem="staffing"
      />
      <PrivateRoute
        exact
        path="/works/staffing_requests/requests"
        component={() => (
          <Navigate to="/works/staffing_requests/requests/long_term" />
        )}
      />
      <PrivateRoute
        path={`/works/staffing_requests/:id(${UuidRegex})/:tabKey(submitted|in-review|offer|accepted|withdrawn|rejected)`}
        component={StaffingRequestPage}
        menuItem="staffing"
      />
      <PrivateRoute
        exact
        path={`/works/staffing_requests/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate to={`/works/staffing_requests/${params.id}/submitted`} />
        )}
      />
      <PrivateRoute
        exact
        path="/works/staffing_requests"
        component={() => <Navigate to="/works/staffing_requests/requests" />}
      />

      <PrivateRoute
        path={`/works/candidates/:id(${UuidRegex})/:tab(summary|notes)`}
        component={CandidatePage}
        menuItem="applications"
      />
      <PrivateRoute
        exact
        path={`/works/candidates/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate to={`/works/candidates/${params.id}/summary`} />
        )}
      />
      <PrivateRoute
        path="/works/candidates"
        component={ApplicationsPage}
        menuItem="applications"
      />

      <PrivateRoute
        path={`/works/applications/:id(${UuidRegex})/:tab(summary|notes)`}
        component={CandidatePage}
        menuItem="applications"
      />
      <PrivateRoute
        exact
        path={`/works/applications/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate to={`/works/applications/${params.id}/summary`} />
        )}
      />
      <PrivateRoute
        path="/works/applications"
        component={ApplicationsPage}
        menuItem="applications"
      />

      <PrivateRoute
        path="/works/reports"
        component={ReportsPage}
        menuItem="reports"
      />

      <PrivateRoute
        exact
        path="/works/account-settings"
        component={() => (
          <Navigate to="/works/account-settings/user-settings" />
        )}
      />
      <PrivateRoute
        path="/works/account-settings/:page(change-password|notifications|user-settings)"
        component={AccountSettingsPage}
      />
      <PrivateRoute
        path="/works/terms-and-conditions"
        component={TermsAndConditionsPage}
        menuItem="admin"
      />

      <PrivateRoute
        path={`/works/billing/invoices/:id(${UuidRegex})/:tabKey(summary|hours|disputes|history)`}
        component={InvoicePage}
        menuItem="billing"
      />
      <PrivateRoute
        exact
        path={`/works/billing/invoices/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate to={`/works/billing/invoices/${params.id}/summary`} />
        )}
      />
      <PrivateRoute
        path="/works/billing/:tab(invoices|disputes)"
        component={BillingPage}
        menuItem="billing"
      />
      <PrivateRoute
        exact
        path="/works/billing"
        component={() => <Navigate to="/works/billing/invoices" />}
      />
      <PrivateRoute
        exact
        path="/works/timecards"
        component={() => <Navigate to="/works/timecards/submitted" />}
        permitted={canShowSidebarTimesheetsItem}
      />
      <PrivateRoute
        path="/works/timecards/:tab(not_submitted|submitted|approved|processing|invoiced|expired|rejected|overdue|all)"
        component={TimesheetsPage}
        menuItem="timecards"
        permitted={canShowSidebarTimesheetsItem}
      />
      <PrivateRoute
        path={`/works/timecard/:id(${UuidRegex})`}
        component={WorkerTimesheetPage}
        menuItem="timecards"
        permitted={canShowSidebarTimesheetsItem}
      />
      {canShowMissionControl ? (
        <>
          <PrivateRoute
            exact
            path="/works"
            component={() => <Navigate to="/works/home/staffing/approvals" />}
            menuItem="home"
          />
          <PrivateRoute
            exact
            path="/works/home"
            component={() => <Navigate to="/works/home/staffing/approvals" />}
            menuItem="home"
          />
          <PrivateRoute
            exact
            path="/works/home/staffing"
            component={() => <Navigate to="/works/home/staffing/approvals" />}
            menuItem="home"
          />
          <PrivateRoute
            exact
            path="/works/home/staffing/:selectedTab(approvals|requests|candidates|reviews|offers)"
            component={MissionControlStaffingPage}
            menuItem="home"
          />
          <PrivateRoute
            exact
            path="/works/home/compliance"
            component={() => <Navigate to="/works/home/compliance/missing" />}
            menuItem="home"
          />
          <PrivateRoute
            exact
            path="/works/home/compliance/:selectedTab(missing|expiring|expired)"
            component={MissionControlCompliancePage}
            menuItem="home"
          />
          <PrivateRoute
            exact
            path="/works/home/billing"
            component={() => <Navigate to="/works/home/billing/open" />}
            menuItem="home"
          />
          <PrivateRoute
            exact
            path="/works/home/billing/:selectedTab(open|approved)"
            component={MissionControlBillingPage}
            menuItem="home"
          />
        </>
      ) : (
        <PrivateRoute
          exact
          path="/works"
          component={HomePage}
          menuItem="home"
        />
      )}
      <PrivateRoute component={NoMatch} menuItem={null} />
      {/**
       * [BEGIN] Legacy URLs redirecting
       * */}
      <PrivateRoute
        exact
        path={`/works/shifts/calendar/view/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate
            to={`/works/shifts/calendar?dialog=shifts-request-details-drawer&shifts-request-details-drawer.shiftId=${params.id}`}
          />
        )}
      />
      {/**
       * [END] Legacy URLs redirecting
       * */}
    </Switch>
  );
}
